/**
 * Cart Product Limit (global)
 *
 * Gates every /cart/add|change|update via fetch + XMLHttpRequest.
 * Products with metafield custom.maximum_quantity_per_order may only have
 * that many units in the cart (all variants share that pool).
 *
 * Limits come from Liquid HTML only:
 * - #CartProductLimits JSON (cart items)
 * - data-maximum-quantity-per-order on product/cart elements
 * JS never writes those values.
 *
 * Cross-tab: BroadcastChannel refreshes drawer UI only.
 * Page-load: seed limits from HTML, fetch /cart.js, trim if over limit.
 *
 * Load before theme.js (layout/theme.liquid).
 */
(function () {
  'use strict';

  /**
   * @param {number|string|null|undefined} qty
   * @returns {string}
   */
  const MSG = (qty) => {
    const n = qty != null && !Number.isNaN(Number(qty)) && Number(qty) > 0 ? Number(qty) : 1;
    const template = window.theme?.strings?.limitOnePerOrder || 'Limit [quantity] per order.';
    return String(template).replace(/\[quantity\]/g, String(n));
  };
  const nativeFetch = window.fetch.bind(window);
  const SYNC_KEY = 'cart-product-limit-sync';
  const TAB_ID = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  /** @type {BroadcastChannel|null} */
  let syncChannel = null;
  try {
    syncChannel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(SYNC_KEY) : null;
  } catch {
    syncChannel = null;
  }
  /** @type {Record<string, number>} */
  const limits = {};
  /** @type {Record<string, string>} */
  const variantToProduct = {};
  let lastCtx = null;
  let lastProductId = null;
  /** Product ids with a drawer limit message that must survive cart rebuilds */
  const drawerErrorIds = new Set();
  let drawerErrorPending = false;
  /** Skip while trimming an over-limit cart */
  let reconciling = false;

  const pid = (el) => el?.dataset?.productId || el?.dataset?.product_id || null;
  const maxAttr = (el) => el?.dataset?.maximumQuantityPerOrder;
  /** @param {string|number|null|undefined} id */
  const limitFor = (id) => {
    if (id == null) return null;
    const max = Number(limits[String(id)]);
    if (!Number.isFinite(max) || max < 1) return null;
    return max;
  };
  const normalizeId = (value) => {
    if (value == null || value === '') return null;
    const id = String(value).includes('/') ? String(value).split('/').pop() : String(value);
    return id && id !== 'null' && id !== 'undefined' ? id : null;
  };

  const registerProduct = (product, setActive = false) => {
    if (!product || product.id == null) return;
    const id = normalizeId(product.id);
    if (!id) return;
    if (product.maximum_quantity_per_order != null && product.maximum_quantity_per_order !== '') {
      limits[id] = Number(product.maximum_quantity_per_order);
    }
    for (const v of product.variants || []) {
      const vid = normalizeId(v?.id);
      if (vid) variantToProduct[vid] = id;
    }
    if (setActive) lastProductId = id;
  };

  const qtyByProduct = (cart) => {
    const map = {};
    for (const item of cart.items || []) {
      const id = String(item.product_id);
      map[id] = (map[id] || 0) + Number(item.quantity || 0);
      if (item.variant_id != null) variantToProduct[String(item.variant_id)] = id;
    }
    return map;
  };

  /** Apply a product_id -> max map into `limits`. */
  const applyLimitMap = (data) => {
    if (!data || typeof data !== 'object') return;
    for (const id of Object.keys(data)) {
      if (data[id] == null || data[id] === '') continue;
      const max = Number(data[id]);
      if (Number.isFinite(max) && max >= 1) limits[String(id)] = max;
    }
  };

  /** Read limits from Liquid-rendered HTML only (never write them). */
  const seed = () => {
    document.querySelectorAll('[data-product-id][data-maximum-quantity-per-order]').forEach((el) => {
      const id = pid(el);
      const raw = maxAttr(el);
      if (id && raw != null && raw !== '') {
        const max = Number(raw);
        if (Number.isFinite(max) && max >= 1) limits[String(id)] = max;
      }
    });

    const node = document.getElementById('CartProductLimits');
    if (node?.textContent) {
      try {
        applyLimitMap(JSON.parse(node.textContent));
      } catch {
        /* ignore */
      }
    }

    document.querySelectorAll('[data-quick-add-product]').forEach((el) => {
      try {
        registerProduct(JSON.parse(el.dataset.quickAddProduct || 'null'));
      } catch {
        /* ignore */
      }
    });
  };

  /** @type {{ items: Array }|null} */
  let cartCache = null;

  const fetchCart = () =>
    nativeFetch(`/cart.js?t=${Date.now()}`, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    }).then((r) => r.json());

  /** Always hit the server for ATC limit checks. */
  const getCart = async () => {
    cartCache = await fetchCart();
    return cartCache;
  };

  /** Promise for the initial page-load trim — cart drawer markup waits on this. */
  let bootTrimPromise = null;

  /** Notify other tabs so they refresh cart UI / re-seed limits. */
  const broadcastCartChanged = () => {
    if (!syncChannel) return;
    try {
      syncChannel.postMessage({ type: 'cart-changed', tabId: TAB_ID, ts: Date.now() });
    } catch {
      /* ignore */
    }
  };

  /** Refresh local cart drawer markup when another tab mutated the cart. */
  const refreshCartFromRemote = () => {
    seed();
    cartCache = null;
    document.dispatchEvent(new CustomEvent('cart:build'));
    document.dispatchEvent(new CustomEvent('cart:updated'));
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  };

  /**
   * Given cart + in-memory limits, POST /cart/update.js for over-limit products.
   * @param {object} cart
   * @param {{ refreshUi?: boolean }} [opts]
   * @returns {Promise<string|null>}
   */
  const trimCartToLimits = async (cart, opts = {}) => {
    /** @type {Record<string, number>} */
    const updates = {};
    let violatedId = null;

    const map = qtyByProduct(cart);
    for (const productId of Object.keys(map)) {
      const max = limitFor(productId);
      if (max == null || map[productId] <= max) continue;

      violatedId = productId;
      let excess = map[productId] - max;
      const lines = (cart.items || [])
        .filter((item) => String(item.product_id) === String(productId))
        .slice()
        .reverse();

      for (const item of lines) {
        if (excess <= 0) break;
        const current = Number(item.quantity || 0);
        if (current <= 0) continue;
        const remove = Math.min(current, excess);
        updates[item.key] = current - remove;
        excess -= remove;
      }
    }

    if (!violatedId || Object.keys(updates).length === 0) return null;

    await nativeFetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates }),
    });

    cartCache = {
      items: (cart.items || [])
        .map((item) => {
          if (updates[item.key] == null) return item;
          return Object.assign({}, item, { quantity: updates[item.key] });
        })
        .filter((item) => Number(item.quantity) > 0),
    };

    lastProductId = violatedId;
    if (opts.refreshUi) {
      drawerErrorIds.add(String(violatedId));
      drawerErrorPending = true;
      if (drawerOpen()) showError();
      document.dispatchEvent(new CustomEvent('cart:build'));
    }
    return violatedId;
  };

  /**
   * Trim cart using Liquid HTML limits + live /cart.js quantities.
   * @param {{ refreshUi?: boolean }} [opts]
   * @returns {Promise<string|null>}
   */
  const reconcileOverLimit = async (opts = {}) => {
    if (reconciling) return null;
    reconciling = true;

    try {
      seed();
      const cart = await fetchCart();
      cartCache = cart;
      if (!cart.items?.length) return null;
      return await trimCartToLimits(cart, opts);
    } catch {
      return null;
    } finally {
      reconciling = false;
    }
  };

  /** After cart mutation: invalidate cache; notify other tabs to refresh UI only. */
  const afterCartMutation = () => {
    cartCache = null;
    broadcastCartChanged();
  };

  const headerContentType = (headers) => {
    if (headers instanceof Headers) return headers.get('Content-Type') || '';
    if (!headers || typeof headers !== 'object') return '';
    const key = Object.keys(headers).find((k) => k.toLowerCase() === 'content-type');
    return key ? String(headers[key] || '') : '';
  };

  const parseBody = (body, headers) => {
    if (!body) return null;
    if (body instanceof URLSearchParams || (typeof FormData !== 'undefined' && body instanceof FormData)) {
      return { id: body.get('id'), quantity: body.get('quantity'), line: body.get('line') };
    }
    if (typeof body !== 'string') return null;
    try {
      const ct = headerContentType(headers);
      if (ct.includes('application/json') || body.trim().startsWith('{')) return JSON.parse(body);
      const p = new URLSearchParams(body);
      return { id: p.get('id'), quantity: p.get('quantity'), line: p.get('line'), updates: null };
    } catch {
      return null;
    }
  };

  const productIdForVariant = (cart, variantId) => {
    const vid = String(variantId);
    if (variantToProduct[vid]) return variantToProduct[vid];

    const line = (cart.items || []).find((i) => String(i.variant_id) === vid);
    if (line) {
      variantToProduct[vid] = String(line.product_id);
      return String(line.product_id);
    }

    // Active quick-add modal is a reliable source for this add
    const modal = document.querySelector('#QuickAddModalProductCards.modal--is-active');
    const fromModal = pid(modal);
    if (fromModal) {
      variantToProduct[vid] = String(fromModal);
      return String(fromModal);
    }

    // Click context — use for this request only; do NOT permanently bind variant → product
    // (that poisoned later adds of other products with the wrong limit)
    const fromCtx = pid(lastCtx?.closest?.('[data-product-id]'));
    if (fromCtx) return String(fromCtx);

    if (lastProductId) return String(lastProductId);

    return null;
  };

  /** Read product/variant ids from Swym wishlist custom element (item.empi / selectedVariant). */
  const captureSwymProductContext = (el) => {
    if (!el) return null;
    const host =
      el.closest?.('swym-storefront-layout-item') ||
      (el.tagName === 'SWYM-STOREFRONT-LAYOUT-ITEM' ? el : null);
    if (!host) return null;

    let item = null;
    let selectedVariant = null;
    try {
      item = host.item;
    } catch {
      /* ignore */
    }
    try {
      selectedVariant = host.selectedVariant;
    } catch {
      /* ignore */
    }

    const empi = normalizeId(
      item?.empi ?? item?.product_id ?? item?.productId ?? host.getAttribute?.('data-product-id'),
    );
    const epi = normalizeId(
      selectedVariant?.id ?? selectedVariant ?? item?.epi ?? item?.variant_id ?? host.getAttribute?.('data-variant-id'),
    );

    if (empi) lastProductId = empi;
    if (empi && epi) variantToProduct[String(epi)] = empi;
    return empi;
  };

  /**
   * When wishlist adds a *different* variant, map it to the product via Swym item
   * or /products/{handle}.js so the product-level limit applies.
   */
  const resolveAddProductId = async (cart, variantId) => {
    const existing = productIdForVariant(cart, variantId);
    if (existing) return existing;

    const fromSwym = captureSwymProductContext(lastCtx);
    if (fromSwym) {
      variantToProduct[String(variantId)] = String(fromSwym);
      return String(fromSwym);
    }

    const host = lastCtx?.closest?.('swym-storefront-layout-item');
    const href =
      host?.querySelector?.('a[href*="/products/"]')?.getAttribute('href') ||
      host?.item?.du ||
      null;
    if (!href) return null;

    const handle = String(href).split('/products/')[1]?.split(/[?#]/)[0];
    if (!handle) return null;

    try {
      const product = await nativeFetch(`/products/${encodeURIComponent(handle)}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      }).then((r) => (r.ok ? r.json() : null));
      if (!product?.id) return null;
      registerProduct(product, true);
      variantToProduct[String(variantId)] = String(product.id);
      return String(product.id);
    } catch {
      return null;
    }
  };

  const maxFor = (productId, variantId) => {
    if (productId != null) {
      const live = document.querySelector(
        `[data-product-id="${productId}"][data-maximum-quantity-per-order]`,
      );
      if (live) {
        const n = Number(maxAttr(live));
        if (Number.isFinite(n) && n >= 1) {
          limits[String(productId)] = n;
          return n;
        }
      }
    }
    if (productId != null && limits[String(productId)] != null) {
      const cached = Number(limits[String(productId)]);
      if (Number.isFinite(cached) && cached >= 1) return cached;
    }
    const scope =
      lastCtx?.closest?.('[data-maximum-quantity-per-order]') ||
      document.querySelector('#QuickAddModalProductCards.modal--is-active[data-maximum-quantity-per-order]') ||
      (variantId &&
        document.querySelector(`[data-variant-id="${variantId}"]`)?.closest('[data-maximum-quantity-per-order]'));
    const raw = maxAttr(scope);
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 1) return null;
    const id = pid(scope) || productId;
    if (id) limits[String(id)] = n;
    return n;
  };

  const ATC_SELECTOR =
    [
      '[data-add-to-cart]',
      '[data-add-to-cart-collection]',
      '[data-mobile-sticky-add-to-cart]',
      '.mobile-menu-trending__add',
      '[data-cart-upsell-add]',
      '[data-quick-add-product]',
      '#QuickAddModalProductCardsAddToCart',
      '.add-to-cart',
      '.js-qty__adjust',
      '.swym-storefront-layout-grid-item-add-to-cart-button',
      '.swym-add-to-cart-btn',
      '.product-collection-showcase__card-add-to-cart',
      '.hero-product-showcase__card-add-to-cart',
      '.landing-favorites-product-card__add-to-cart',
      '[class*="__card-add-to-cart"]',
      '.boost-sd__button',
      'form[action*="/cart/add"] [type="submit"]',
      'button[name="add"]',
    ].join(', ');

  const wouldBlockAdd = async (cart, body, headers) => {
    const data = parseBody(body, headers);
    const map = qtyByProduct(cart);

    // Wishlist / unknown body: use last clicked product if it is already at limit
    if (!data?.id && !data?.items?.length) {
      captureSwymProductContext(lastCtx);
      const max = limitFor(lastProductId);
      return max != null && (map[String(lastProductId)] || 0) >= max;
    }

    const entries = Array.isArray(data.items)
      ? data.items
      : data.id != null
        ? [{ id: data.id, quantity: data.quantity || 1 }]
        : [];

    for (const entry of entries) {
      const variantId = String(entry.id);
      const qty = Number(entry.quantity || 1);
      const productId = await resolveAddProductId(cart, variantId);
      const max = maxFor(productId, variantId);
      if (max == null || !Number.isFinite(Number(max)) || Number(max) < 1) continue;
      const limit = Number(max);

      if (!productId) {
        if (qty > limit) return true;
        continue;
      }

      if ((map[String(productId)] || 0) + qty > limit) return true;
      map[String(productId)] = (map[String(productId)] || 0) + qty;
    }
    return false;
  };

  const wouldBlockChange = (cart, body, headers) => {
    const data = parseBody(body, headers);
    if (data?.quantity == null) return false;
    const nextQty = Number(data.quantity);
    if (Number.isNaN(nextQty)) return false;

    let line = null;
    if (data.id) {
      line = (cart.items || []).find(
        (i) => String(i.key) === String(data.id) || String(i.variant_id) === String(data.id),
      );
    } else if (data.line) {
      line = (cart.items || [])[Number(data.line) - 1];
    }
    const max = line ? limitFor(line.product_id) : null;
    if (!line || max == null) return false;
    if (nextQty > max) return true;

    let total = 0;
    for (const item of cart.items || []) {
      if (String(item.product_id) !== String(line.product_id)) continue;
      total += item.key === line.key ? nextQty : Number(item.quantity || 0);
    }
    return total > max;
  };

  const wouldBlockUpdates = (cart, updates) => {
    const simulated = {};
    for (const item of cart.items || []) {
      const id = String(item.product_id);
      const q = updates[String(item.variant_id)] ?? updates[item.key] ?? item.quantity ?? 0;
      simulated[id] = (simulated[id] || 0) + Number(q);
    }
    return Object.keys(simulated).some((id) => {
      const max = limitFor(id);
      return max != null && simulated[id] > max;
    });
  };

  const ensureStyle = () => {
    if (document.getElementById('cart-limit-style')) return;
    const el = document.createElement('style');
    el.id = 'cart-limit-style';
    el.textContent =
      '.cart-product-limit-message{color:#d02e2e;margin:6px 0 0;padding:0;border:0;background:none;font-size:12px;line-height:1.3;font-weight:600;text-align:center}' +
      '#CartDrawer .cart-product-limit-message,.cart__item .cart-product-limit-message{text-align:left}' +
      /* Direct ATC: sit at bottom of card info (below price / color swatches) */
      '.product-collection-showcase__card-meta > .cart-product-limit-message,' +
      '.hero-product-showcase__card-meta > .cart-product-limit-message,' +
      '.landing-favorites-product-card__card-meta > .cart-product-limit-message,' +
      '.grid-product__content > .cart-product-limit-message,' +
      '.boost-sd__product-info > .cart-product-limit-message,' +
      '.boost-sd__suggestion-queries-item--product > .cart-product-limit-message,' +
      '.boost-sd__suggestion-queries-item-product--right > .cart-product-limit-message,' +
      '[class*="__card-meta"] > .cart-product-limit-message{display:block;width:100%;margin:3px 0 0;text-align:left}' +
      /* Mobile menu trending: under Add to cart */
      '.mobile-menu-trending__add + .cart-product-limit-message,' +
      '.mobile-menu-trending__meta > .cart-product-limit-message{display:block;width:100%;margin:2px 0 0;text-align:left;font-size:11px}' +
      '.mobile-sticky-cart-bar > .cart-product-limit-message{display:block;width:100%;margin:0 0 10px;font-size:12px;line-height:1.3;text-align:center}' +
      /* Wishlist: keep ATC buttons aligned — message sits below without shifting the button */
      '.swym-storefront-layout-grid-item-action-container{position:relative}' +
      '.swym-storefront-layout-grid-item,.swym-storefront-layout-grid-item-action-container{overflow:visible}' +
      '.swym-storefront-layout-grid-item-action-container .cart-product-limit-message,' +
      'swym-storefront-layout-item .cart-product-limit-message{' +
      'position:unset;left:0;right:0;top:100%;margin:10px 0 0;z-index:2;pointer-events:none}' ;
    document.head.appendChild(el);
  };

  const drawerOpen = () => document.getElementById('CartDrawer')?.classList.contains('drawer--is-open');

  /**
   * @param {string|null|undefined} productId
   * @param {string} [customText]
   */
  const createMessage = (productId, customText) => {
    const id = productId || lastProductId;
    const max = limitFor(id) || 1;
    const p = document.createElement('p');
    p.className = 'cart-product-limit-message';
    p.setAttribute('role', 'alert');
    p.textContent = customText || MSG(max);
    if (id) p.dataset.limitProductId = String(id);
    return p;
  };

  /** Remove Impulse bordered `.errors` boxes that duplicate our limit message. */
  const stripThemeErrors = () => {
    document.querySelectorAll('.product-single__form .errors, form[action*="/cart/add"] .errors, #QuickAddModalProductCards .errors').forEach((n) => {
      if (/limit\s+\d+\s+per order/i.test(n.textContent || '')) n.remove();
    });
  };

  const clearNonDrawerMessages = () => {
    document.querySelectorAll('.cart-product-limit-message').forEach((n) => {
      if (!n.closest('#CartDrawer, .cart__item')) n.remove();
    });
  };

  const mountDrawerMessage = (productId) => {
    const id = productId ? String(productId) : lastProductId ? String(lastProductId) : null;
    if (!id) return false;

    drawerErrorIds.add(id);
    drawerErrorPending = true;

    document
      .querySelectorAll(`#CartDrawer .cart-product-limit-message[data-limit-product-id="${id}"], .cart__item .cart-product-limit-message[data-limit-product-id="${id}"]`)
      .forEach((n) => n.remove());

    const wrap =
      document.querySelector(
        `#CartDrawer .js-qty__wrapper[data-product-id="${id}"], .cart__item .js-qty__wrapper[data-product-id="${id}"]`,
      ) ||
      (lastCtx && document.body.contains(lastCtx) ? lastCtx.closest('.js-qty__wrapper') : null);

    if (!wrap?.parentNode) return false;
    wrap.after(createMessage(id));
    return true;
  };

  const reattachDrawerErrors = () => {
    if (!drawerErrorPending || !drawerOpen()) return;
    for (const id of drawerErrorIds) {
      if (document.querySelector(`#CartDrawer .cart-product-limit-message[data-limit-product-id="${id}"]`)) continue;
      mountDrawerMessage(id);
    }
  };

  const clearDrawerErrors = () => {
    drawerErrorPending = false;
    drawerErrorIds.clear();
    document.querySelectorAll('#CartDrawer .cart-product-limit-message, .cart__item .cart-product-limit-message').forEach((n) => n.remove());
  };

  /** Place limit message at the bottom of a product card's info (below price/colors). */
  const mountCardLimitMessage = (ctx, messageEl) => {
    if (!ctx?.closest) return false;

    // Prefer the full card/li — never the Boost image column
    // (.boost-sd__suggestion-queries-item-product--left), which clips overflow.
    const card = ctx.closest(
      [
        '.product-collection-showcase__card',
        '.hero-product-showcase__card',
        '.landing-favorites-product-card__card',
        '.landing-favorites-product-card',
        '.grid-product',
        '.boost-sd__product-item',
        '.boost-sd__suggestion-queries-item--product',
        '.boost-sd__suggestion-queries-item.boost-sd__suggestion-queries-item--product',
      ].join(', '),
    );
    if (!card) return false;

    card.querySelectorAll('.cart-product-limit-message').forEach((n) => n.remove());

    // Boost instant search: text lives in --right; `a` uses overflow:hidden so
    // append onto the li after the link so the message is never clipped.
    if (
      card.classList.contains('boost-sd__suggestion-queries-item--product') ||
      card.matches?.('.boost-sd__suggestion-queries-item--product')
    ) {
      const right = card.querySelector('.boost-sd__suggestion-queries-item-product--right');
      if (right) {
        right.appendChild(messageEl);
        return true;
      }
      card.appendChild(messageEl);
      return true;
    }

    let mountTarget = null;
    if (card.classList.contains('grid-product')) {
      mountTarget = card.querySelector('.grid-product__content') || card;
    } else {
      mountTarget =
        card.querySelector(
          [
            '.product-collection-showcase__card-meta',
            '.hero-product-showcase__card-meta',
            '.landing-favorites-product-card__card-meta',
            '.boost-sd__product-info',
            '[class*="__card-meta"]',
          ].join(', '),
        ) || card;
    }

    mountTarget.appendChild(messageEl);
    return true;
  };

  /**
   * Place an ATC error after the last clicked control (no bordered box).
   * @param {Element} [forceCtx] optional click target (e.g. direct-add button in search)
   * @param {string} [customMessage] Shopify / inventory message; omit for limit copy
   */
  const showError = (forceCtx, customMessage) => {
    ensureStyle();
    if (!customMessage || /limit\s+\d+\s+per order/i.test(customMessage)) {
      stripThemeErrors();
    }

    if (forceCtx instanceof Element) {
      lastCtx = forceCtx;
      const fromBtn =
        pid(forceCtx) ||
        pid(forceCtx.closest('[data-product-id]')) ||
        normalizeId(forceCtx.getAttribute?.('data-product-id'));
      if (fromBtn) lastProductId = String(fromBtn);
      try {
        const raw = forceCtx.getAttribute?.('data-quick-add-product');
        if (raw) registerProduct(JSON.parse(raw.replace(/&quot;/g, '"')), true);
      } catch {
        /* ignore broken Boost JSON */
      }
    }

    const ctx = lastCtx && document.body.contains(lastCtx) ? lastCtx : null;
    const qtyWrap = ctx?.closest?.('.js-qty__wrapper');
    const fromCart = Boolean(qtyWrap || ctx?.closest?.('#CartDrawer, .cart__item'));

    if (fromCart) {
      // Do not alter cart drawer for non-limit errors (inventory etc.)
      if (customMessage && !/limit\s+\d+\s+per order/i.test(customMessage)) {
        return;
      }
      const productId =
        lastProductId ||
        pid(qtyWrap) ||
        pid(ctx?.closest?.('[data-product-id]'));
      mountDrawerMessage(productId);
      requestAnimationFrame(() => requestAnimationFrame(reattachDrawerErrors));
      return;
    }

    clearNonDrawerMessages();
    const p = createMessage(lastProductId, customMessage);

    const modal = document.querySelector('#QuickAddModalProductCards.modal--is-active');
    const stickyContent = document.querySelector(
      '[data-mobile-sticky-cart-bar][data-visible="true"] .mobile-sticky-cart-bar__content',
    );
    const form =
      ctx?.closest?.('form.product-single__form, form[action*="/cart/add"]') ||
      document.querySelector('.product-single__form');
    // PDP: sit directly under the Add to cart button (not under Shop Pay / payment block)
    const addToCartBtn = form?.querySelector('[data-add-to-cart]');
    const swymBtn =
      ctx?.closest?.('.swym-storefront-layout-grid-item-add-to-cart-button, .swym-add-to-cart-btn') ||
      (ctx?.matches?.('.swym-storefront-layout-grid-item-add-to-cart-button, .swym-add-to-cart-btn') ? ctx : null);

    // Mobile menu trending: message directly under the Add to cart button
    const trendingBtn =
      ctx?.closest?.('.mobile-menu-trending__add') ||
      (ctx?.matches?.('.mobile-menu-trending__add') ? ctx : null);
    if (trendingBtn) {
      const card = trendingBtn.closest('.mobile-menu-trending__card');
      if (card) card.querySelectorAll('.cart-product-limit-message').forEach((n) => n.remove());
      trendingBtn.after(p);
      return;
    }

    // Direct ATC on collection/showcase/search cards: message at bottom of card info
    if (!modal && ctx && mountCardLimitMessage(ctx, p)) return;

    if (swymBtn) {
      const action = swymBtn.closest('.swym-storefront-layout-grid-item-action-container');
      if (action) action.appendChild(p);
      else swymBtn.after(p);
      return;
    }
    if (modal) {
      const addBtn = modal.querySelector('#QuickAddModalProductCardsAddToCart');
      if (addBtn && addBtn.style.display !== 'none') addBtn.after(p);
      else modal.appendChild(p);
      return;
    }
    if (stickyContent) {
      stickyContent.after(p);
      return;
    }
    if (addToCartBtn) {
      addToCartBtn.after(p);
      return;
    }
    if (form) {
      form.appendChild(p);
      return;
    }
    if (ctx) {
      ctx.after(p);
      return;
    }
    document.body.appendChild(p);
  };

  const json = (payload) =>
    new Response(JSON.stringify(payload), {
      status: 200,
      headers: { 'Content-Type': 'application/json' },
    });

  const failAdd = () => {
    const max = limitFor(lastProductId) || 1;
    const message = MSG(max);
    showError();
    // Body status 422 stops theme success path; strip bordered .errors it creates next tick
    requestAnimationFrame(stripThemeErrors);
    document.dispatchEvent(new CustomEvent('cart:updated'));
    return json({ status: 422, message, description: message, limitError: true });
  };

  const failChange = (cart) => {
    showError();
    document.dispatchEvent(new CustomEvent('cart:updated'));
    requestAnimationFrame(() => requestAnimationFrame(reattachDrawerErrors));
    return json(cart);
  };

  const isCartUrl = (url) => {
    const path = String(url || '').split('?')[0];
    return {
      path,
      add: path.includes('/cart/add'),
      change: path.includes('/cart/change'),
      update: path.includes('/cart/update'),
      any: path.includes('/cart/add') || path.includes('/cart/change') || path.includes('/cart/update'),
    };
  };

  /**
   * Only serialize cart writes. Concurrent add/change/update is what triggers
   * Shopify's "upstream connect error". Reads wait behind an in-flight write.
   * @type {Promise<void>}
   */
  let writeQueue = Promise.resolve();
  let writesInFlight = 0;

  /**
   * @template T
   * @param {() => Promise<T>} task
   * @returns {Promise<T>}
   */
  const enqueueWrite = (task) => {
    const run = writeQueue.then(
      () => {
        writesInFlight += 1;
        return task().finally(() => {
          writesInFlight -= 1;
        });
      },
      () => {
        writesInFlight += 1;
        return task().finally(() => {
          writesInFlight -= 1;
        });
      },
    );
    writeQueue = run.then(
      () => {},
      () => {},
    );
    return run;
  };

  /** @returns {Promise<void>} */
  const waitForWrites = () => {
    if (writesInFlight <= 0) return Promise.resolve();
    return writeQueue;
  };

  /**
   * Must include both nodes theme.CartForm.cartMarkup expects.
   * Missing .cart__discounts makes discounts.append(null) throw and Safari
   * white-screens / reloads — especially on the first PLP ATC.
   */
  const EMPTY_AJAX_CART_HTML =
    '<div class="cart__items" data-count="0" data-cart-subtotal="0" data-cart-savings="0"></div>' +
    '<div class="cart__discounts cart__item-sub cart__item-row hide"></div>';

  /** @param {string} text */
  const isValidAjaxCartHtml = (text) =>
    typeof text === 'string' &&
    text.includes('cart__items') &&
    text.includes('cart__discounts') &&
    !/upstream connect error|remote connection failure|disconnect\/reset before headers/i.test(text);

  /**
   * Retry only on gateway failures / network throws. Do not clone+read every
   * body (that OOM-crashes mobile when view=ajax HTML is large).
   * Never reject after retries — theme AjaxProduct has no .catch and will crash.
   * @param {RequestInfo|URL} input
   * @param {RequestInit} [opts]
   * @param {{ asJson?: boolean }} [fallback]
   * @returns {Promise<Response>}
   */
  const fetchWithRetry = async (input, opts, fallback = {}) => {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const response = await nativeFetch(input, opts);
        if (response.status !== 502 && response.status !== 503 && response.status !== 504) {
          return response;
        }
      } catch {
        /* retry */
      }

      await new Promise((resolve) => setTimeout(resolve, 250 * (attempt + 1)));
    }

    if (fallback.asJson) {
      const message = 'Cart temporarily unavailable. Please try again.';
      return new Response(JSON.stringify({ status: 422, message, description: message }), {
        status: 422,
        headers: { 'Content-Type': 'application/json' },
      });
    }

    return new Response(EMPTY_AJAX_CART_HTML, {
      status: 200,
      headers: { 'Content-Type': 'text/html' },
    });
  };

  /**
   * theme.CartForm.cartMarkup crashes if view=ajax HTML has no .cart__items /
   * .cart__discounts. Retry, then fall back to safe empty markup.
   * @param {Response} response
   * @param {RequestInfo|URL} input
   * @param {RequestInit} [opts]
   * @returns {Promise<Response>}
   */
  const ensureAjaxCartMarkup = async (response, input, opts) => {
    const text = await response.text();
    if (isValidAjaxCartHtml(text)) {
      return new Response(text, {
        status: 200,
        headers: { 'Content-Type': 'text/html' },
      });
    }

    for (let attempt = 0; attempt < 2; attempt += 1) {
      await new Promise((resolve) => setTimeout(resolve, 300 * (attempt + 1)));
      try {
        const retry = await nativeFetch(input, opts);
        const retryText = await retry.text();
        if (isValidAjaxCartHtml(retryText)) {
          return new Response(retryText, {
            status: 200,
            headers: { 'Content-Type': 'text/html' },
          });
        }
      } catch {
        /* try again */
      }
    }

    return new Response(EMPTY_AJAX_CART_HTML, {
      status: 200,
      headers: { 'Content-Type': 'text/html' },
    });
  };

  const gateCartRequest = async (url, body, headers) => {
    const { add, change, update } = isCartUrl(url);
    if (!add && !change && !update) return { block: false };

    seed();

    let cart;
    try {
      cart = await getCart();
    } catch {
      // If /cart.js fails, do not block the write — blocking here freezes ATC/remove.
      return { block: false };
    }

    if (add && (await wouldBlockAdd(cart, body, headers))) {
      return { block: true, type: 'add', cart };
    }

    if (change && wouldBlockChange(cart, body, headers)) {
      return { block: true, type: 'change', cart };
    }

    if (update) {
      const data = parseBody(body, headers);
      if (data && !data.updates && data.quantity == null && !data.id) {
        return { block: false };
      }
      if (data?.updates && wouldBlockUpdates(cart, data.updates)) {
        return { block: true, type: 'change', cart };
      }
    }

    return { block: false };
  };

  window.theme = window.theme || {};
  theme.showCartLimitError = showError;
  /** @param {Element} [ctx] @param {string} message */
  theme.showCartAddError = (ctx, message) => {
    if (!message) return;
    showError(ctx instanceof Element ? ctx : undefined, String(message));
  };

  // Remember click target so the error can sit near the control that triggered the add
  document.addEventListener(
    'click',
    (e) => {
      const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
      const el =
        path.find((n) => n instanceof Element) || (e.target instanceof Element ? e.target : null);
      if (!el) return;

      const btn =
        path.find((n) => n instanceof Element && n.matches?.(ATC_SELECTOR)) ||
        el.closest(ATC_SELECTOR);

      if (btn) {
        lastCtx = btn;
        const swymPid = captureSwymProductContext(btn);
        const modal = document.querySelector('#QuickAddModalProductCards.modal--is-active');
        lastProductId =
          swymPid ||
          pid(btn.closest('.js-qty__wrapper')) ||
          pid(btn) ||
          pid(btn.closest('[data-product-id]')) ||
          pid(modal) ||
          normalizeId(btn.dataset?.empi) ||
          lastProductId;

        const limitScope =
          btn.closest('[data-maximum-quantity-per-order]') ||
          (modal?.hasAttribute?.('data-maximum-quantity-per-order') ? modal : null);
        const raw = maxAttr(limitScope);
        if (lastProductId && raw != null && raw !== '') {
          limits[String(lastProductId)] = Number(raw);
        }
      }

      const scope = el.closest('[data-quick-add-product], [data-maximum-quantity-per-order]');
      if (!scope) return;
      const id = pid(scope);
      const raw = maxAttr(scope);
      if (id && raw != null && raw !== '') limits[String(id)] = Number(raw);
      if (id) lastProductId = String(id);
      try {
        registerProduct(JSON.parse(scope.dataset.quickAddProduct || 'null'), true);
      } catch {
        /* ignore */
      }
    },
    true,
  );

  document.addEventListener('quickadd:product', (event) => {
    if (event?.detail?.product) registerProduct(event.detail.product, true);
  });

  // Theme creates bordered `.errors` on data.status === 422 — for limit, strip duplicate;
  // for other errors (inventory), surface on sticky bar when that UI is active.
  document.addEventListener('ajaxProduct:error', (event) => {
    const msg = String(event?.detail?.errorMessage || '');
    if (!msg) return;
    const isLimit = /limit\s+\d+\s+per order/i.test(msg);

    requestAnimationFrame(() => {
      if (isLimit) {
        stripThemeErrors();
        if (!document.querySelector('.cart-product-limit-message')) showError();
        return;
      }

      const stickyVisible = document.querySelector(
        '[data-mobile-sticky-cart-bar][data-visible="true"] .mobile-sticky-cart-bar__content',
      );
      if (stickyVisible) {
        showError(lastCtx, msg);
      }
    });
  });

  document.addEventListener('cart:updated', () => {
    seed();
    reattachDrawerErrors();
    requestAnimationFrame(reattachDrawerErrors);
  });

  document.addEventListener('cart:refresh', () => {
    requestAnimationFrame(() => requestAnimationFrame(reattachDrawerErrors));
  });

  /** Coalesce burst rebuilds after qty/remove so mobile does not fetch view=ajax 5x. */
  let ajaxMarkupInflight = null;
  let ajaxMarkupQueued = false;

  /** @param {string} html */
  const ajaxCartResponse = (html) =>
    new Response(html || EMPTY_AJAX_CART_HTML, {
      status: 200,
      headers: { 'Content-Type': 'text/html' },
    });

  // Global fetch gate: serialize writes; hold reads only while a write is in flight.
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : input?.url || '';
    const opts = init || {};
    const cartUrl = isCartUrl(url);
    const isAjaxMarkup = String(url).includes('view=ajax');
    const isCartJs = /\/cart\.js(?:$|\?)/.test(String(url).split('#')[0]);

    if (!cartUrl.any && !isAjaxMarkup && !isCartJs) {
      return nativeFetch(input, opts);
    }

    if (cartUrl.any) {
      return enqueueWrite(async () => {
        const result = await gateCartRequest(url, opts.body, opts.headers);
        if (result.block) {
          return result.type === 'add' ? failAdd() : failChange(result.cart);
        }

        const response = await fetchWithRetry(input, opts, { asJson: true });
        if (response.ok && !reconciling) {
          if (cartUrl.add) clearNonDrawerMessages();
          afterCartMutation();
          // First PLP ATC: invalidate any in-flight empty-cart markup fetch so
          // buildCart never paints pre-add HTML (Safari white-screen / reload).
          if (ajaxMarkupInflight) ajaxMarkupQueued = true;
        }
        return response;
      });
    }

    // Cart reads: wait for page-load trim and any in-flight write, then go.
    const afterGates = async () => {
      if (bootTrimPromise && isAjaxMarkup) await bootTrimPromise;
      await waitForWrites();

      if (isAjaxMarkup) {
        // Share HTML text (not Response.clone — clone can throw on iOS).
        if (ajaxMarkupInflight) {
          ajaxMarkupQueued = true;
          const html = await ajaxMarkupInflight;
          return ajaxCartResponse(html);
        }

        ajaxMarkupInflight = (async () => {
          try {
            let html = EMPTY_AJAX_CART_HTML;

            // Always wait for writes before each attempt. The first PLP ATC
            // races page-load buildCart (empty) against post-add rebuild —
            // do/while guarantees we refetch after the add lands.
            do {
              ajaxMarkupQueued = false;
              await waitForWrites();
              let response = await fetchWithRetry(input, opts);
              response = await ensureAjaxCartMarkup(response, input, opts);
              html = await response.text();
            } while (ajaxMarkupQueued);

            return isValidAjaxCartHtml(html) ? html : EMPTY_AJAX_CART_HTML;
          } catch {
            return EMPTY_AJAX_CART_HTML;
          }
        })().finally(() => {
          ajaxMarkupInflight = null;
        });

        const html = await ajaxMarkupInflight;
        return ajaxCartResponse(html);
      }

      return fetchWithRetry(input, opts, { asJson: isCartJs });
    };

    return afterGates();
  };

  // Global XHR gate (Swym wishlist uses XHR for /cart/add.js)
  const xhrOpen = XMLHttpRequest.prototype.open;
  const xhrSend = XMLHttpRequest.prototype.send;
  const xhrHeader = XMLHttpRequest.prototype.setRequestHeader;

  const finishXhr = (xhr, payload, status, ok) => {
    const body = typeof payload === 'string' ? payload : JSON.stringify(payload);
    try {
      Object.defineProperty(xhr, 'readyState', { configurable: true, get: () => 4 });
      Object.defineProperty(xhr, 'status', { configurable: true, get: () => status });
      Object.defineProperty(xhr, 'statusText', { configurable: true, get: () => (ok ? 'OK' : 'Error') });
      Object.defineProperty(xhr, 'responseText', { configurable: true, get: () => body });
      Object.defineProperty(xhr, 'response', { configurable: true, get: () => body });
    } catch {
      /* ignore */
    }
    const fire = (type, handler) => {
      if (typeof handler === 'function') {
        try {
          handler.call(xhr, new Event(type));
        } catch {
          handler.call(xhr);
        }
      }
      try {
        xhr.dispatchEvent(new Event(type));
      } catch {
        /* ignore */
      }
    };
    fire('readystatechange', xhr.onreadystatechange);
    if (ok) fire('load', xhr.onload);
    else fire('error', xhr.onerror);
    fire('loadend', xhr.onloadend);
  };

  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this._cartLimitUrl = url;
    this._cartLimitHeaders = {};
    return xhrOpen.apply(this, [method, url, ...rest]);
  };

  XMLHttpRequest.prototype.setRequestHeader = function (name, value) {
    if (!this._cartLimitHeaders) this._cartLimitHeaders = {};
    this._cartLimitHeaders[name] = value;
    return xhrHeader.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function (body) {
    const url = this._cartLimitUrl || '';
    const cartUrl = isCartUrl(url);
    if (!cartUrl.any) return xhrSend.apply(this, arguments);

    const xhr = this;
    const args = arguments;
    const headers = this._cartLimitHeaders || {};

    enqueueWrite(
      () =>
        new Promise((resolve) => {
          let settled = false;
          const settle = () => {
            if (settled) return;
            settled = true;
            resolve();
          };

          xhr.addEventListener('loadend', settle, { once: true });

          (async () => {
            try {
              const result = await gateCartRequest(url, body, headers);
              if (!result.block) {
                xhr.addEventListener(
                  'load',
                  () => {
                    if (xhr.status >= 200 && xhr.status < 300 && !reconciling) {
                      afterCartMutation();
                    }
                  },
                  { once: true },
                );
                xhrSend.apply(xhr, args);
                return;
              }

              xhr.removeEventListener('loadend', settle);
              if (result.type === 'add') {
                const max = limitFor(lastProductId) || 1;
                const message = MSG(max);
                showError();
                document.dispatchEvent(new CustomEvent('cart:updated'));
                // error (not load) so Swym does not treat the add as successful
                finishXhr(xhr, { status: 422, message, description: message, limitError: true }, 422, false);
              } else {
                showError();
                document.dispatchEvent(new CustomEvent('cart:updated'));
                requestAnimationFrame(() => requestAnimationFrame(reattachDrawerErrors));
                finishXhr(xhr, result.cart, 200, true);
              }
              settle();
            } catch (error) {
              try {
                xhrSend.apply(xhr, args);
              } catch {
                settle();
              }
            }
          })();
        }),
    );
  };

  /**
   * Quick view injects a product form before AjaxProduct is bound.
   * The first submit would otherwise be a full navigation (page reload).
   * Drawer crash guards live in cart-drawer.liquid (always-on).
   */
  const patchAjaxProduct = () => {
    if (!window.theme || typeof theme.AjaxProduct !== 'function' || theme.AjaxProduct.__cartQueuePatched) return;

    const Original = theme.AjaxProduct;

    function PatchedAjaxProduct(form, submit, args) {
      if (form instanceof HTMLFormElement) form.dataset.ajaxCartBound = 'true';
      return new Original(form, submit, args);
    }

    PatchedAjaxProduct.prototype = Original.prototype;
    PatchedAjaxProduct.__cartQueuePatched = true;
    theme.AjaxProduct = PatchedAjaxProduct;
  };

  document.addEventListener('page:loaded', patchAjaxProduct);

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.closest('.modal--quick-shop')) return;
      if (form.dataset.ajaxCartBound === 'true') return;

      event.preventDefault();
      event.stopPropagation();

      const button = form.querySelector('[data-add-to-cart], button[type="submit"], [type="submit"]');
      if (button instanceof HTMLButtonElement) {
        button.disabled = true;
        button.classList.add('btn--loading');
      }

      const cartAdd = window.theme?.routes?.cartAdd || '/cart/add.js';
      const body = new URLSearchParams(new FormData(form));

      window
        .fetch(cartAdd, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: body.toString(),
        })
        .then((response) => response.json())
        .then((data) => {
          if (data && data.status === 422) {
            document.dispatchEvent(
              new CustomEvent('ajaxProduct:error', {
                detail: { errorMessage: data.description || data.message || '' },
                bubbles: true,
              }),
            );
            return;
          }

          document.dispatchEvent(
            new CustomEvent('ajaxProduct:added', {
              detail: { product: data },
              bubbles: true,
            }),
          );
          // Do not also dispatch cart:open — ajaxProduct:added already opens
          // the drawer; a second open races the first PLP rebuild on Safari.
          document.dispatchEvent(new CustomEvent('cart:updated'));
        })
        .catch(() => {})
        .finally(() => {
          if (button instanceof HTMLButtonElement) {
            button.disabled = false;
            button.classList.remove('btn--loading');
          }
        });
    },
    true,
  );

  const boot = () => {
    ensureStyle();
    seed();

    // Other tabs: refresh drawer UI only — do NOT trim with this tab's possibly stale limits
    if (syncChannel) {
      syncChannel.onmessage = (event) => {
        if (event?.data?.tabId === TAB_ID) return;
        if (event?.data?.type !== 'cart-changed') return;
        refreshCartFromRemote();
      };
    }

    const drawer = document.getElementById('CartDrawer');
    if (drawer && drawer.dataset.limitObs !== 'true') {
      drawer.dataset.limitObs = 'true';
      new MutationObserver(() => {
        if (!drawerOpen()) {
          clearDrawerErrors();
          return;
        }
        if (!drawerErrorPending) return;
        reattachDrawerErrors();
      }).observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    }
  };

  // Page-load: read Liquid limits from HTML, then trim cart if over limit
  bootTrimPromise = enqueueWrite(() => reconcileOverLimit({ refreshUi: true }))
    .then((violated) => {
      if (violated) document.dispatchEvent(new CustomEvent('cart:build'));
    })
    .finally(() => {
      bootTrimPromise = null;
    });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
